import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarPlus, CheckCircle2, Circle, Loader2, Monitor, Play, XCircle, MinusCircle } from 'lucide-react';
import { evaluateQaTenantAccess, resolveQaTenantKey } from '../qa/tenantAccess';
import { healthRowsPending } from '../qa/healthChecks';
import { MODULE_FILTER_ID, moduleRowsPending } from '../qa/moduleChecks';
import {
  demoApi,
  employeeApi,
  locationApi,
  qaUiTestApi,
  type DemoDataBackfillResult,
  type QaUiTestCapabilities,
  type QaUiTestRun,
} from '../services/api';
import {
  buildInitialRows,
  isTimecardFilter,
  runQaSuite,
  scenarioGroup,
  suiteScenarios,
  type QaSuiteMode,
} from '../qa/runQaSuite';
import { qaTimecardConfig } from '../qa/timecardScenarios/scenarioCatalog';
import { appConfirm } from '../context/SystemDialogContext';
import { mergeTenantDefaults } from '../qa/timecardScenarios/sandboxIds';
import type { QaCheckRow, QaCheckStatus } from '../qa/timecardScenarios/types';

const GROUP_FILTERS: { id: QaSuiteMode; label: string }[] = [
  { id: 'all', label: 'Run all checks' },
  { id: 'health', label: 'Health only' },
  { id: MODULE_FILTER_ID, label: 'Modules' },
  { id: 'sync', label: 'Timecard sync' },
  { id: 'break', label: 'Timecard breaks' },
  { id: 'match', label: 'Timecard match' },
  { id: 'approval', label: 'Timecard approval' },
];

function StatusIcon({ status }: { status: QaCheckStatus }) {
  if (status === 'passed') return <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />;
  if (status === 'failed') return <XCircle className="h-4 w-4 text-red-600 shrink-0" />;
  if (status === 'skipped') return <MinusCircle className="h-4 w-4 text-neutral-400 shrink-0" />;
  if (status === 'running') return <Loader2 className="h-4 w-4 text-blue-600 shrink-0 animate-spin" />;
  return <Circle className="h-4 w-4 text-neutral-300 shrink-0" />;
}

function statusLabel(status: QaCheckStatus): string {
  if (status === 'passed') return 'PASS';
  if (status === 'failed') return 'FAIL';
  if (status === 'skipped') return 'SKIP';
  if (status === 'running') return '…';
  return '';
}

const QaChecks: React.FC = () => {
  const access = useMemo(() => evaluateQaTenantAccess(), []);
  const sandbox = useMemo(
    () => mergeTenantDefaults(qaTimecardConfig, resolveQaTenantKey(access.tenantCode, access.tenantName)),
    [access.tenantCode, access.tenantName]
  );
  const [mode, setMode] = useState<QaSuiteMode>('all');
  const [rows, setRows] = useState<QaCheckRow[]>(() => buildInitialRows('all', sandbox));
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [sandboxNames, setSandboxNames] = useState<{ employee: string; location: string } | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<DemoDataBackfillResult | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const [uiCaps, setUiCaps] = useState<QaUiTestCapabilities | null>(null);
  const [uiCapsError, setUiCapsError] = useState<string | null>(null);
  const [uiPassword, setUiPassword] = useState('');
  const [uiRun, setUiRun] = useState<QaUiTestRun | null>(null);
  const [uiStartError, setUiStartError] = useState<string | null>(null);
  const [uiStarting, setUiStarting] = useState(false);

  useEffect(() => {
    if (!access.allowed) return;
    let cancelled = false;
    (async () => {
      try {
        const [emp, loc] = await Promise.all([
          employeeApi.getById(sandbox.employeeId),
          locationApi.getById(sandbox.locationId),
        ]);
        if (cancelled) return;
        const employee = [emp.firstName, emp.lastName].filter(Boolean).join(' ').trim() || emp.email;
        setSandboxNames({ employee, location: loc.name });
      } catch {
        if (!cancelled) setSandboxNames(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [access.allowed, sandbox.employeeId, sandbox.locationId]);

  useEffect(() => {
    if (!access.allowed) return;
    let cancelled = false;
    (async () => {
      try {
        const caps = await qaUiTestApi.capabilities();
        if (!cancelled) {
          setUiCaps(caps);
          setUiCapsError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setUiCaps(null);
          setUiCapsError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [access.allowed]);

  const uiRunActive = uiRun?.status === 'queued' || uiRun?.status === 'running';

  useEffect(() => {
    if (!uiRunActive || !uiRun?.runId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await qaUiTestApi.getRun(uiRun.runId);
        if (!cancelled) setUiRun(next);
      } catch {
        // Keep the last snapshot; the next interval retries.
      }
    };
    const id = window.setInterval(tick, 1000);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [uiRunActive, uiRun?.runId]);

  const handleStartUiTest = async () => {
    if (uiStarting || uiRunActive || running || !access.allowed) return;
    setUiStarting(true);
    setUiStartError(null);
    try {
      const started = await qaUiTestApi.start(uiCaps?.hasPassword ? undefined : uiPassword);
      setUiRun(started);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      setUiStartError(
        axiosErr.response?.data?.message ||
          (err instanceof Error ? err.message : String(err))
      );
    } finally {
      setUiStarting(false);
    }
  };

  const summary = useMemo(() => {
    const passed = rows.filter((r) => r.status === 'passed').length;
    const failed = rows.filter((r) => r.status === 'failed').length;
    const skipped = rows.filter((r) => r.status === 'skipped').length;
    const pending = rows.filter((r) => r.status === 'pending' || r.status === 'running').length;
    return { passed, failed, skipped, pending, total: rows.length };
  }, [rows]);

  const groups = useMemo(() => {
    const map = new Map<string, QaCheckRow[]>();
    for (const row of rows) {
      const list = map.get(row.group) || [];
      list.push(row);
      map.set(row.group, list);
    }
    return Array.from(map.entries());
  }, [rows]);

  const applyMode = (next: QaSuiteMode) => {
    if (running) return;
    setMode(next);
    setRows(buildInitialRows(next, sandbox));
    setRunError(null);
  };

  const handleBackfillDemoData = async () => {
    if (backfilling || running || !access.allowed) return;
    const confirmed = await appConfirm(
      `Fill empty days through today for ${access.tenantName || access.tenantCode || 'this tenant'}?\n\n` +
        'Existing days are left unchanged. New days get cloned shifts and attendance, then timecard sync fills scheduled, actual, and paid times. Completed weeks are approved only after paid in and paid out are both set. Open punches on finished shifts are closed near the shift end time.'
    );
    if (!confirmed) return;
    setBackfilling(true);
    setBackfillError(null);
    setBackfillResult(null);
    try {
      const result = await demoApi.backfillUntilToday();
      setBackfillResult(result);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      setBackfillError(
        axiosErr.response?.data?.message ||
          (err instanceof Error ? err.message : String(err))
      );
    } finally {
      setBackfilling(false);
    }
  };

  const handleRun = async () => {
    if (running || !access.allowed) return;
    setRunning(true);
    setRunError(null);
    setRows(buildInitialRows(mode, sandbox));
    try {
      await runQaSuite(mode, (row) => {
        setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));
      });
    } catch (err: unknown) {
      setRunError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  if (!access.allowed) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-semibold text-neutral-900 mb-2">QA Checks</h1>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900">This page is not available here</p>
            <p className="text-sm text-amber-800 mt-1">{access.reason}</p>
          </div>
        </div>
      </div>
    );
  }

  const scenarioCount = suiteScenarios().filter(
    (s) => mode === 'all' || mode === 'health' || scenarioGroup(s) === mode
  ).length;
  const moduleCount = moduleRowsPending(mode === MODULE_FILTER_ID ? MODULE_FILTER_ID : mode === 'all' ? 'all' : mode).length;
  const runLabel =
    mode === 'health'
      ? 'Run health checks'
      : mode === MODULE_FILTER_ID
        ? 'Run module checks'
        : 'Run checks';
  const countHint =
    mode === 'health'
      ? `${healthRowsPending().length} health`
      : mode === MODULE_FILTER_ID
        ? `${moduleCount} module`
        : mode === 'all'
          ? `${moduleCount} module · ${scenarioCount} timecard`
          : isTimecardFilter(mode)
            ? `${scenarioCount} scenario(s)`
            : `${moduleCount} module`;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900">QA Checks</h1>
        <p className="text-sm text-neutral-600 mt-1">
          Verify this Staffly tenant is healthy, then smoke-test every module, then run the
          automated timecard scenarios. Signed in as{' '}
          <span className="font-medium">{access.tenantName || access.tenantCode}</span>.
          Run all checks will prepare seed, clean leftover data, then test.
        </p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-6 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          Timecard scenarios write sandbox data for{' '}
          <span className="font-medium">{sandboxNames?.employee || 'the QA sandbox employee'}</span> at{' '}
          <span className="font-medium">{sandboxNames?.location || 'the sandbox location'}</span>.
          Existing employee, location, position, and schedule records are reused (not duplicated).
          Prepare assigns anything still missing, aligns weeks to the tenant week-start day, removes
          leftover punches, then writes a trial shift and punch. Tests start only if that trial
          produces timecard rows and a paid-break shift sets paidBreakPaid. Module checks are
          read-only lists and reports. Discover never picks other employees. This will not run
          on staffly.pro.
        </p>
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white p-4 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-neutral-800">Demo data through today</h2>
            <p className="text-sm text-neutral-600 mt-1">
              After a one-time seed, later days stay empty. This clones each employee&apos;s recent
              shift pattern onto empty days through today for{' '}
              <span className="font-medium">{access.tenantName || access.tenantCode}</span>
              . Matching punches are added, then timecard sync fills scheduled, actual, and paid
              times. Completed weeks are approved only when those paid times are set. Existing days
              are not changed. Open punches on finished shifts are closed near the shift end time.
            </p>
          </div>
          <button
            type="button"
            onClick={handleBackfillDemoData}
            disabled={backfilling || running}
            className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2 shrink-0"
          >
            {backfilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
            {backfilling ? 'Filling…' : 'Fill empty days through today'}
          </button>
        </div>
        {backfillError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {backfillError}
          </div>
        )}
        {backfillResult && (
          <div
            className={`mt-3 rounded-md border px-3 py-2 text-sm ${
              backfillResult.alreadyCurrent
                ? 'border-neutral-200 bg-neutral-50 text-neutral-700'
                : 'border-teal-200 bg-teal-50 text-teal-900'
            }`}
          >
            <p>{backfillResult.message}</p>
            {!backfillResult.alreadyCurrent && (
              <p className="mt-1 text-xs text-teal-800">
                {backfillResult.employeesConsidered} employee(s) · {backfillResult.shiftsInserted}{' '}
                shifts · {backfillResult.attendanceInserted} punches ·{' '}
                {backfillResult.timecardDaysInserted} timecard days · {backfillResult.weeksApproved}{' '}
                week(s) approved · {backfillResult.weeksPublished} week(s) published
                {backfillResult.openPunchesClosed
                  ? ` · ${backfillResult.openPunchesClosed} open punch(es) closed`
                  : ''}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white p-4 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-neutral-800">Browser UI smoke</h2>
            <p className="text-sm text-neutral-600 mt-1">
              In-app launch through the API is unreliable on IIS (no desktop for Chrome). Use the
              desktop runner instead: <span className="font-mono text-xs">run-staffly-ui-tests.ps1</span> or{' '}
              <span className="font-mono text-xs">dotnet run --project Staffly.UiTestRunner</span>. That
              opens Playwright as your Windows user so login and Timecards run in a real browser.
            </p>
          </div>
          <button
            type="button"
            onClick={handleStartUiTest}
            disabled={
              uiStarting ||
              uiRunActive ||
              running ||
              !uiCaps?.enabled ||
              (!uiCaps.hasPassword && !uiPassword.trim())
            }
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 shrink-0"
          >
            {uiStarting || uiRunActive ? <Loader2 className="h-4 w-4 animate-spin" /> : <Monitor className="h-4 w-4" />}
            {uiStarting || uiRunActive ? 'Running browser…' : 'Run browser smoke'}
          </button>
        </div>
        {uiCapsError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            Could not load browser-test status from the API. Restart Staffly.API after this change. {uiCapsError}
          </div>
        )}
        {uiCaps && !uiCaps.enabled && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {uiCaps.blockedReason ||
              'Browser UI tests are not available on this host. Use localhost or LAN Staffly, not staffly.pro.'}
          </div>
        )}
        {uiCaps?.enabled && !uiCaps.hasPassword && (
          <div className="mt-3">
            <label htmlFor="qa-ui-password" className="block text-xs font-medium text-neutral-600 mb-1">
              Password for the browser login (not saved)
            </label>
            <input
              id="qa-ui-password"
              type="password"
              autoComplete="current-password"
              value={uiPassword}
              onChange={(e) => setUiPassword(e.target.value)}
              disabled={uiStarting || uiRunActive}
              className="w-full max-w-sm px-3 py-2 text-sm border border-neutral-300 rounded-md"
              placeholder="Same password you use on /login"
            />
          </div>
        )}
        {uiStartError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {uiStartError}
          </div>
        )}
        {uiRun && (
          <div className="mt-3">
            <p className="text-sm text-neutral-700">
              {uiRun.status === 'passed' && 'Browser smoke passed.'}
              {uiRun.status === 'failed' && (uiRun.message || 'Browser smoke failed.')}
              {(uiRun.status === 'queued' || uiRun.status === 'running') &&
                (uiRun.message || 'Chromium is starting on the API machine…')}
            </p>
            <ul className="mt-2 divide-y divide-neutral-100 border border-neutral-200 rounded-md overflow-hidden">
              {uiRun.steps.map((step) => (
                <li key={step.id} className="px-3 py-2 flex items-start gap-3 bg-white">
                  <StatusIcon status={step.status} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-neutral-900">{step.title}</span>
                      {step.status !== 'pending' && (
                        <span
                          className={`text-xs font-semibold ${
                            step.status === 'passed'
                              ? 'text-green-700'
                              : step.status === 'failed'
                                ? 'text-red-700'
                                : step.status === 'skipped'
                                  ? 'text-neutral-500'
                                  : 'text-blue-700'
                          }`}
                        >
                          {statusLabel(step.status)}
                        </span>
                      )}
                    </div>
                    {step.message && (
                      <p className="text-xs text-neutral-600 mt-0.5 break-words">{step.message}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {GROUP_FILTERS.map((g) => (
          <button
            key={g.id}
            type="button"
            disabled={running}
            onClick={() => applyMode(g.id)}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              mode === g.id
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
            } disabled:opacity-50`}
          >
            {g.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <button
          type="button"
          onClick={handleRun}
          disabled={running}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {running ? 'Running…' : runLabel}
        </button>
        <div className="text-sm text-neutral-600">
          {summary.passed} passed · {summary.failed} failed · {summary.skipped} skipped
          {summary.pending > 0 ? ` · ${summary.pending} remaining` : ''}
          <span className="text-neutral-400">
            {' '}
            ({countHint})
          </span>
        </div>
      </div>

      {runError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {runError}
        </div>
      )}

      <div className="space-y-6">
        {groups.map(([group, groupRows]) => (
          <section key={group} className="border border-neutral-200 rounded-lg bg-white overflow-hidden">
            <h2 className="px-4 py-2 text-sm font-semibold text-neutral-700 bg-neutral-50 border-b border-neutral-200 capitalize">
              {group}
            </h2>
            <ul className="divide-y divide-neutral-100">
              {groupRows.map((row: QaCheckRow) => (
                <li key={row.id} className="px-4 py-2.5 flex items-start gap-3">
                  <StatusIcon status={row.status} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-mono text-neutral-500">{row.id}</span>
                      <span className="text-sm font-medium text-neutral-900">{row.title}</span>
                      {row.status !== 'pending' && (
                        <span
                          className={`text-xs font-semibold ${
                            row.status === 'passed'
                              ? 'text-green-700'
                              : row.status === 'failed'
                                ? 'text-red-700'
                                : row.status === 'skipped'
                                  ? 'text-neutral-500'
                                  : 'text-blue-700'
                          }`}
                        >
                          {statusLabel(row.status)}
                        </span>
                      )}
                    </div>
                    {row.message && (
                      <p className="text-xs text-neutral-600 mt-0.5 break-words">{row.message}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
};

export default QaChecks;
